import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import type { Session, User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import { onSessionExpired } from '../lib/sessionExpiry';
import { POST_LOGIN_PATH } from '../constants/dashboard';

interface AuthContextValue {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /** The API rejected our token; the stored session is gone and the user must sign in again. */
  sessionExpired: boolean;
  signInWithGoogle: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // Read from the auth listener, which is registered once and would otherwise close
  // over a stale `sessionExpired`.
  const expiredRef = useRef(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setLoading(false);
    });

    const { data: listener } = supabase.auth.onAuthStateChange((_event, newSession) => {
      // After expiry we keep the last session in state on purpose: the sign-out below
      // would otherwise null the user, and `RequireAuth` would replace the page with
      // /login before the dialog could insist on an acknowledgement. Nothing here is
      // usable — the stored session is already gone, so every request still 401s.
      if (expiredRef.current) return;
      setSession(newSession);
    });

    return () => listener.subscription.unsubscribe();
  }, []);

  useEffect(
    () =>
      onSessionExpired(() => {
        // Requests in flight when the token died all 401 together; the first one wins.
        if (expiredRef.current) return;
        expiredRef.current = true;
        setSessionExpired(true);

        // Clear the stored session now rather than when the dialog's button is clicked,
        // so a second tab opened in the meantime finds nothing in localStorage and has
        // to sign in. auth-js ignores the logout endpoint's own 401 and wipes storage
        // regardless, which is exactly the case we are in here.
        void supabase.auth.signOut();
      }),
    []
  );

  const signInWithGoogle = useCallback(async () => {
    await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: `${window.location.origin}${POST_LOGIN_PATH}` },
    });
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      sessionExpired,
      signInWithGoogle,
      signOut,
    }),
    [session, loading, sessionExpired, signInWithGoogle, signOut]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
